import { useState } from 'react';
import { startOAuthSignIn } from '../../api/authApi';

// Minimal, official-shape brand marks inlined as SVG rather than pulling in
// an icon-package dependency for just these four logos. Sized to sit inside
// an 18x18 box.

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.87 2.7-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.98v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.98A9 9 0 0 0 0 9c0 1.45.35 2.83.98 4.03l2.97-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .98 4.97l2.97 2.33C4.66 5.17 6.65 3.58 9 3.58z" />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect x="0" y="0" width="8.5" height="8.5" fill="#F25022" />
      <rect x="9.5" y="0" width="8.5" height="8.5" fill="#7FBA00" />
      <rect x="0" y="9.5" width="8.5" height="8.5" fill="#00A4EF" />
      <rect x="9.5" y="9.5" width="8.5" height="8.5" fill="#FFB900" />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg width="16" height="18" viewBox="0 0 16 18" aria-hidden="true" fill="currentColor">
      <path d="M13.15 9.53c-.02-2.03 1.66-3 1.74-3.05-.95-1.38-2.42-1.57-2.95-1.6-1.26-.13-2.45.74-3.09.74-.64 0-1.62-.72-2.67-.7-1.37.02-2.64.8-3.34 2.02-1.43 2.47-.37 6.13 1.02 8.14.68.98 1.5 2.09 2.57 2.05 1.03-.04 1.42-.67 2.67-.67 1.24 0 1.6.67 2.68.65 1.11-.02 1.81-1 2.48-1.99.78-1.14 1.1-2.25 1.11-2.31-.02-.01-2.13-.82-2.15-3.28zM11.15 2.6c.56-.68.94-1.62.83-2.6-.8.03-1.79.53-2.37 1.2-.52.6-.98 1.56-.85 2.5.92.07 1.86-.47 2.39-1.1z" />
    </svg>
  );
}

function LinkedInMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect width="18" height="18" rx="3" fill="#0A66C2" />
      <path
        fill="#fff"
        d="M5.34 7.1H2.98v7.7h2.36V7.1zM4.16 6.1c.8 0 1.3-.53 1.3-1.19-.02-.68-.5-1.19-1.28-1.19-.79 0-1.3.51-1.3 1.19 0 .66.5 1.19 1.27 1.19h.01zM6.7 14.8h2.36v-4.3c0-.23.02-.46.09-.63.18-.46.6-.94 1.31-.94.93 0 1.3.71 1.3 1.74v4.13h2.36v-4.4c0-2.18-1.16-3.2-2.72-3.2-1.25 0-1.81.7-2.12 1.18h.02V7.1H6.7c.03.66 0 7.7 0 7.7z"
      />
    </svg>
  );
}

const PROVIDERS = [
  { id: 'google', label: 'Google', Mark: GoogleMark },
  { id: 'microsoft', label: 'Microsoft', Mark: MicrosoftMark },
  { id: 'apple', label: 'Apple', Mark: AppleMark },
  { id: 'linkedin', label: 'LinkedIn', Mark: LinkedInMark },
] as const;

// Authorization Code flow, no client-side SDKs - see backend/modules/
// oauth/transitions/oauth.go's header comment for why. Clicking a button
// fetches a ready-made authorize URL and navigates the WHOLE page there
// (not a popup); every provider redirects back to
// /oauth/callback/{provider} (OAuthCallbackScreen.tsx), which is where the
// sign-in actually completes. `state` is stashed in sessionStorage here so
// that screen can confirm the redirect it's handling is the one this tab
// itself started, not a replayed/forged one.
export function SocialLoginButtons() {
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const [error, setError] = useState('');

  const start = async (provider: string) => {
    setLoadingProvider(provider);
    setError('');
    try {
      const { url, state } = await startOAuthSignIn(provider);
      sessionStorage.setItem('oauth.state', state);
      sessionStorage.setItem('oauth.provider', provider);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to continue with ${provider}.`);
      setLoadingProvider(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 text-xs text-black/40 dark:text-white/40">
        <div className="flex-1 h-px bg-black/10 dark:bg-white/10" />
        or continue with
        <div className="flex-1 h-px bg-black/10 dark:bg-white/10" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {PROVIDERS.map(({ id, label, Mark }) => (
          <button
            key={id}
            type="button"
            onClick={() => start(id)}
            disabled={loadingProvider !== null}
            className="flex items-center justify-center gap-2 rounded-xl border border-black/15 dark:border-white/15 px-3 py-2.5 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mark />
            {loadingProvider === id ? 'Redirecting…' : label}
          </button>
        ))}
      </div>
      {error && <span className="text-sm text-red-500 text-center">{error}</span>}
    </div>
  );
}
